import { useState, MouseEvent } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Breadcrumbs,
  Link,
  Typography,
  Box,
  IconButton,
  Avatar,
  Badge,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Notifications,
  Logout,
  Lock,
  HomeOutlined,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getRoleLabel } from './menuConfig';

interface HeaderProps {
  topOffset?: number;
  onMenuClick?: () => void;
}

const PATH_LABELS: Record<string, string> = {
  '': 'Dashboard',
  empresas: 'Empresas',
  'mi-empresa': 'Mi Empresa',
  afiliados: 'Afiliados',
  periodos: 'Periodos',
  ddjj: 'Declaraciones Juradas',
  cupones: 'Cupones',
  pagos: 'Pagos',
  'cuenta-corriente': 'Cuenta Corriente',
  transacciones: 'Transacciones',
  cvus: 'Gestion CVUs',
  acreditacion: 'Acreditacion Automatica',
  reportes: 'Reportes',
  usuarios: 'Usuarios',
  configuracion: 'Configuracion',
  'conceptos-aporte': 'Conceptos de aporte',
  'cambiar-contrasena': 'Cambiar Contrasena',
  nuevo: 'Nuevo',
  editar: 'Editar',
};

// UUID v4 regex (formato standard, 8-4-4-4-12 hex chars)
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Genera los breadcrumbs desde el pathname.
 * - Reemplaza UUIDs por "Detalle"
 * - Si el UUID viene seguido de una accion (ej: /:id/editar), se omite
 *   el segmento del UUID para no mostrar redundancia ("Detalle / Editar")
 * - Las rutas alias (ej: /mi-empresa) se muestran con su label legible
 */
export function getBreadcrumbs(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [{ label: 'Dashboard', path: '/' }];

  // Si la ruta es algo tipo /empresas/:uuid/editar, queremos:
  //   Empresas / Editar
  // En vez de:
  //   Empresas / UUID / Editar
  // Por eso filtramos primero los UUIDs cuando hay un segmento siguiente
  // que es una accion conocida (editar, eliminar, etc.).
  const ACTIONS = new Set(['editar', 'nuevo', 'eliminar']);

  const result: Array<{ label: string; path: string }> = [];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const next = segments[i + 1];
    const path = '/' + segments.slice(0, i + 1).join('/');

    if (UUID_REGEX.test(seg)) {
      // Si despues del UUID viene una accion, saltamos este segmento.
      // El proximo paso del bucle pondra "Editar"/"Nuevo" directamente.
      if (next && ACTIONS.has(next)) continue;
      // Sino, es una vista de detalle: mostramos "Detalle"
      result.push({ label: 'Detalle', path });
      continue;
    }

    const label = PATH_LABELS[seg] ?? seg;
    result.push({ label, path });
  }

  return result;
}

export default function Header({ topOffset = 0, onMenuClick }: HeaderProps) {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  const handleOpenMenu = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleCloseMenu();
    await signOut();
    navigate('/auth/login', { replace: true });
  };

  const breadcrumbs = getBreadcrumbs(location.pathname);
  // En mobile mostramos solo el ultimo breadcrumb (titulo de la pagina)
  const visibleBreadcrumbs = isSmall ? breadcrumbs.slice(-1) : breadcrumbs;

  const initials = profile
    ? `${profile.nombre?.[0] ?? ''}${profile.apellido?.[0] ?? ''}`.toUpperCase() || 'U'
    : 'U';

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        mt: `${topOffset}px`,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
          {isMobile && (
            <IconButton
              onClick={onMenuClick}
              edge="start"
              size="medium"
              sx={{ mr: 1 }}
              aria-label="Abrir menu"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Breadcrumbs
            separator="/"
            aria-label="breadcrumb"
            sx={{
              minWidth: 0,
              '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
              '& .MuiBreadcrumbs-li': {
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          >
            {!isSmall && (
              <Link
                component={RouterLink}
                to="/"
                underline="hover"
                color="inherit"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <HomeOutlined sx={{ mr: 0.5, fontSize: 18 }} />
                Inicio
              </Link>
            )}
            {visibleBreadcrumbs.map((bc, i) =>
              i === visibleBreadcrumbs.length - 1 ? (
                <Typography key={bc.path} color="text.primary" fontWeight={500} noWrap>
                  {bc.label}
                </Typography>
              ) : (
                <Link
                  key={bc.path}
                  component={RouterLink}
                  to={bc.path}
                  underline="hover"
                  color="inherit"
                >
                  {bc.label}
                </Link>
              )
            )}
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Notificaciones">
            <IconButton size="medium">
              <Badge badgeContent={0} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          {!isSmall && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}

          <IconButton
            onClick={handleOpenMenu}
            aria-label="Menu de usuario"
            sx={{ p: 0.5, gap: 1 }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
              {initials}
            </Avatar>
            {!isMobile && (
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2" fontWeight={500} color="text.primary" noWrap>
                  {profile?.nombre} {profile?.apellido ?? ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {profile ? getRoleLabel(profile.rol) : ''}
                </Typography>
              </Box>
            )}
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: { sx: { minWidth: 240, mt: 1 } },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" noWrap>
                {profile?.nombre} {profile?.apellido ?? ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {profile?.email}
              </Typography>
              <Typography variant="caption" display="block" color="primary" fontWeight={600}>
                {profile ? getRoleLabel(profile.rol) : ''}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              component={RouterLink}
              to="/cambiar-contrasena"
              onClick={handleCloseMenu}
            >
              <ListItemIcon>
                <Lock fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cambiar contrasena</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Cerrar sesion</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
